<template>
  <div class="admin-page">
    <div class="admin-toolbar">
      <div class="admin-toolbar__title">
        <h2>角色与权限</h2>
        <p class="page-desc">管理 RBAC 角色，并为角色绑定 API 权限（permission_name，与接口路径点分格式一致）</p>
      </div>
      <div class="admin-toolbar__actions">
        <el-button type="primary" @click="openCreateRole">新建角色</el-button>
        <el-button @click="fetchRoles">刷新</el-button>
      </div>
    </div>

    <el-table :data="roles" v-loading="loadingRoles" style="width: 100%">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="name" label="角色名" min-width="140" />
      <el-table-column prop="description" label="描述" min-width="200" show-overflow-tooltip />
      <el-table-column label="类型" width="88">
        <template #default="scope">
          <el-tag v-if="isBuiltinRoleName(scope.row.name)" size="small" type="info">内置</el-tag>
          <span v-else class="text-muted">自定义</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="220" fixed="right">
        <template #default="scope">
          <el-button size="small" @click="openEditRole(scope.row)">编辑 / 权限</el-button>
          <el-button
            size="small"
            type="danger"
            :disabled="isBuiltinRoleName(scope.row.name)"
            @click="handleDeleteRole(scope.row)"
          >删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-divider />

    <div class="admin-toolbar">
      <div class="admin-toolbar__title">
        <h3>权限定义</h3>
        <p class="page-desc">
          permission_name 与接口路径点分格式一致；新增 / 修改 / 删除权限仅超级管理员可操作
        </p>
      </div>
      <div class="admin-toolbar__actions">
        <el-button v-if="isSuperAdmin" type="primary" @click="openCreatePermission">新增权限</el-button>
        <el-button @click="fetchPermissions">刷新</el-button>
      </div>
    </div>

    <el-table :data="permissions" v-loading="loadingPerms" style="width: 100%" max-height="360">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="name" label="permission_name" min-width="220" />
      <el-table-column prop="description" label="描述" min-width="200" show-overflow-tooltip />
      <el-table-column v-if="isSuperAdmin" label="操作" width="160" fixed="right">
        <template #default="scope">
          <el-button size="small" @click="openEditPermission(scope.row)">编辑</el-button>
          <el-button size="small" type="danger" @click="handleDeletePermission(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      v-model="roleDialogVisible"
      :title="roleForm.id ? '编辑角色' : '新建角色'"
      width="920px"
      destroy-on-close
      @closed="resetRoleForm"
    >
      <el-form :model="roleForm" label-width="88px">
        <el-form-item label="角色名" required>
          <el-input
            v-model="roleForm.name"
            placeholder="如 custom-editor"
            :disabled="roleFormIsBuiltin"
          />
          <p v-if="roleFormIsBuiltin" class="form-hint">内置角色名称不可修改，仅可调整描述与权限</p>
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="roleForm.description" type="textarea" :rows="2" />
        </el-form-item>
        <el-form-item label="权限" class="form-item-transfer">
          <div class="transfer-wrap">
            <el-transfer
              v-model="roleForm.permissionNames"
              :data="permissionTransferData"
              :titles="['可选权限', '已绑定']"
              filterable
              filter-placeholder="搜索权限"
            />
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="roleDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="savingRole" @click="saveRole">保存</el-button>
      </template>
    </el-dialog>

    <el-dialog
      v-model="permDialogVisible"
      :title="permForm.id ? '编辑权限' : '新增权限'"
      width="480px"
      destroy-on-close
      @closed="resetPermForm"
    >
      <el-form :model="permForm" label-width="88px">
        <el-form-item label="标识" required>
          <el-input v-model="permForm.code" placeholder="admin.user.list" :disabled="!!permForm.id" />
        </el-form-item>
        <el-form-item label="名称" required>
          <el-input v-model="permForm.name" placeholder="与标识相同" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="permForm.description" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="permDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="savingPerm" @click="savePermission">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  listRoles,
  listPermissions,
  getRole,
  createRole,
  updateRole,
  deleteRole,
  createPermission,
  updatePermission,
  deletePermission
} from '@/utils/rbacApi'
import { isBuiltinRoleName, BUILTIN_ROLE_NAMES, isSuperAdmin as checkSuperAdmin } from '@/utils/sysRole'

export default {
  name: 'RolePermissionManagement',
  setup() {
    const loadingRoles = ref(false)
    const loadingPerms = ref(false)
    const savingRole = ref(false)
    const savingPerm = ref(false)
    const roles = ref([])
    const permissions = ref([])
    const roleDialogVisible = ref(false)
    const permDialogVisible = ref(false)
    const roleForm = ref({
      id: 0,
      name: '',
      description: '',
      permissionNames: []
    })
    const permForm = ref({
      id: 0,
      code: '',
      name: '',
      description: ''
    })

    const isSuperAdmin = computed(() => {
      try {
        const user = JSON.parse(localStorage.getItem('user_info') || '{}')
        return checkSuperAdmin(user.sysRole)
      } catch {
        return false
      }
    })

    const roleFormIsBuiltin = computed(() => isBuiltinRoleName(roleForm.value.name))

    const permissionTransferData = computed(() =>
      permissions.value.map((p) => ({
        key: p.name,
        label: p.description ? `${p.name} — ${p.description}` : p.name
      }))
    )

    const fetchPermissions = async () => {
      loadingPerms.value = true
      try {
        const res = await listPermissions()
        if (res.code === 200) {
          permissions.value = res.data?.permissions || []
        } else {
          ElMessage.error(res.message || '获取权限列表失败')
        }
      } catch (e) {
        console.error(e)
        ElMessage.error('获取权限列表失败')
      } finally {
        loadingPerms.value = false
      }
    }

    const fetchRoles = async () => {
      loadingRoles.value = true
      try {
        const res = await listRoles()
        if (res.code === 200) {
          roles.value = res.data?.roles || []
        } else {
          ElMessage.error(res.message || '获取角色列表失败')
        }
      } catch (e) {
        console.error(e)
        ElMessage.error('获取角色列表失败')
      } finally {
        loadingRoles.value = false
      }
    }

    const resetRoleForm = () => {
      roleForm.value = { id: 0, name: '', description: '', permissionNames: [] }
    }

    const openCreateRole = async () => {
      resetRoleForm()
      if (!permissions.value.length) await fetchPermissions()
      roleDialogVisible.value = true
    }

    const openEditRole = async (row) => {
      if (!permissions.value.length) await fetchPermissions()
      try {
        const res = await getRole(row.id)
        if (res.code !== 200) {
          ElMessage.error(res.message || '加载角色失败')
          return
        }
        const bound = (res.data?.permissions || []).map((p) => p.name)
        roleForm.value = {
          id: row.id,
          name: res.data?.role?.name || row.name,
          description: res.data?.role?.description || row.description || '',
          permissionNames: bound
        }
        roleDialogVisible.value = true
      } catch (e) {
        console.error(e)
        ElMessage.error('加载角色失败')
      }
    }

    const saveRole = async () => {
      if (!roleForm.value.name?.trim()) {
        ElMessage.warning('请填写角色名')
        return
      }
      savingRole.value = true
      try {
        const payload = {
          name: roleForm.value.name.trim(),
          description: roleForm.value.description || '',
          permissions: roleForm.value.permissionNames || []
        }
        let res
        if (roleForm.value.id) {
          res = await updateRole({ id: roleForm.value.id, ...payload })
        } else {
          res = await createRole(payload)
        }
        if (res.code === 200) {
          ElMessage.success('保存成功')
          roleDialogVisible.value = false
          fetchRoles()
        } else {
          ElMessage.error(res.message || '保存失败')
        }
      } catch (e) {
        console.error(e)
        ElMessage.error('保存失败')
      } finally {
        savingRole.value = false
      }
    }

    const handleDeleteRole = async (row) => {
      if (isBuiltinRoleName(row.name)) {
        ElMessage.warning('系统内置角色不可删除')
        return
      }
      try {
        await ElMessageBox.confirm(`确定删除角色「${row.name}」？`, '确认', { type: 'warning' })
        const res = await deleteRole(row.id)
        if (res.code === 200) {
          ElMessage.success('已删除')
          fetchRoles()
        } else {
          ElMessage.error(res.message || '删除失败')
        }
      } catch (e) {
        if (e !== 'cancel') {
          console.error(e)
          ElMessage.error('删除失败')
        }
      }
    }

    const resetPermForm = () => {
      permForm.value = { id: 0, code: '', name: '', description: '' }
    }

    const openCreatePermission = () => {
      if (!isSuperAdmin.value) {
        ElMessage.warning('仅超级管理员可新增权限')
        return
      }
      resetPermForm()
      permDialogVisible.value = true
    }

    const openEditPermission = (row) => {
      if (!isSuperAdmin.value) {
        ElMessage.warning('仅超级管理员可修改权限')
        return
      }
      permForm.value = {
        id: row.id,
        code: row.name,
        name: row.name,
        description: row.description || ''
      }
      permDialogVisible.value = true
    }

    const savePermission = async () => {
      if (!isSuperAdmin.value) {
        ElMessage.warning('仅超级管理员可操作')
        return
      }
      const code = permForm.value.code?.trim()
      const name = (permForm.value.name?.trim() || code)
      if (!name) {
        ElMessage.warning('请填写权限名称')
        return
      }
      if (!permForm.value.id && !code) {
        ElMessage.warning('请填写权限标识')
        return
      }
      savingPerm.value = true
      try {
        let res
        if (permForm.value.id) {
          res = await updatePermission({
            id: permForm.value.id,
            name,
            code: name,
            description: permForm.value.description || ''
          })
        } else {
          res = await createPermission({
            code: code || name,
            name,
            description: permForm.value.description || ''
          })
        }
        if (res.code === 200) {
          ElMessage.success(permForm.value.id ? '权限已更新' : '权限已创建')
          permDialogVisible.value = false
          fetchPermissions()
        } else {
          ElMessage.error(res.message || '保存失败')
        }
      } catch (e) {
        console.error(e)
        ElMessage.error('保存失败')
      } finally {
        savingPerm.value = false
      }
    }

    const handleDeletePermission = async (row) => {
      if (!isSuperAdmin.value) {
        ElMessage.warning('仅超级管理员可删除权限')
        return
      }
      try {
        await ElMessageBox.confirm(`确定删除权限「${row.name}」？关联角色的绑定将一并移除。`, '确认', {
          type: 'warning'
        })
        const res = await deletePermission(row.id)
        if (res.code === 200) {
          ElMessage.success('已删除')
          fetchPermissions()
        } else {
          ElMessage.error(res.message || '删除失败')
        }
      } catch (e) {
        if (e !== 'cancel') {
          console.error(e)
          ElMessage.error('删除失败')
        }
      }
    }

    onMounted(() => {
      fetchRoles()
      fetchPermissions()
    })

    return {
      loadingRoles,
      loadingPerms,
      savingRole,
      savingPerm,
      roles,
      permissions,
      roleDialogVisible,
      permDialogVisible,
      roleForm,
      roleFormIsBuiltin,
      permForm,
      permissionTransferData,
      isBuiltinRoleName,
      BUILTIN_ROLE_NAMES,
      isSuperAdmin,
      fetchRoles,
      fetchPermissions,
      openCreateRole,
      openEditRole,
      saveRole,
      handleDeleteRole,
      resetRoleForm,
      openCreatePermission,
      openEditPermission,
      savePermission,
      handleDeletePermission,
      resetPermForm
    }
  }
}
</script>

<style scoped>
.admin-toolbar {
  justify-content: space-between;
  align-items: flex-start;
}
.admin-toolbar__title {
  flex: 1;
  min-width: 0;
}
.page-desc,
.form-hint {
  margin: 4px 0 0;
  font-size: 13px;
  color: #909399;
}
.text-muted {
  font-size: 12px;
  color: #909399;
}
.admin-toolbar__title h2,
.admin-toolbar__title h3 {
  margin: 0;
  font-size: 18px;
}
.admin-toolbar__title h3 {
  font-size: 16px;
}
/* 权限穿梭框：标签置顶 + 全宽，避免两列被挤成上下两行 */
.form-item-transfer {
  display: block;
}
:deep(.form-item-transfer .el-form-item__label) {
  display: block;
  width: 100% !important;
  text-align: left;
  height: auto;
  line-height: 1.5;
  padding-bottom: 8px;
}
:deep(.form-item-transfer .el-form-item__content) {
  margin-left: 0 !important;
  width: 100%;
  max-width: 100%;
}
.transfer-wrap {
  width: 100%;
  overflow-x: auto;
}
:deep(.transfer-wrap .el-transfer) {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
}
:deep(.transfer-wrap .el-transfer-panel) {
  width: 360px;
  flex: 0 0 360px;
}
:deep(.transfer-wrap .el-transfer__buttons) {
  flex: 0 0 auto;
  padding: 0 12px;
  align-self: center;
}
</style>
